import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { Offer, OfferLine } from "@/hooks/use-offers-queries";
import type { OfferSection } from "@/hooks/use-offer-sections";
import {
  buildDuplicatedLineInserts,
  buildDuplicatedOfferInsert,
  buildDuplicatedSectionInserts,
} from "@/lib/duplicate-offer";

/**
 * VIB-2088: kopier et tilbud (linjer + tilvalg) til et nytt utkast eid av
 * aktiv bruker. Hopper over useCreateOffer's auto-linjer — kilden er
 * sannheten, ellers dobles reisetid/tilbudsskriving.
 */
export function useDuplicateOffer() {
  const queryClient = useQueryClient();
  const { activeUser } = useImpersonation();

  return useMutation({
    mutationFn: async (sourceOfferId: string): Promise<Offer> => {
      if (!activeUser?.id) throw new Error("Ingen aktiv bruker");

      const { data: source, error: sourceErr } = await supabase
        .from("offers")
        .select("*")
        .eq("id", sourceOfferId)
        .single();
      if (sourceErr) throw sourceErr;

      const { data: sections, error: sectionErr } = await supabase
        .from("offer_sections")
        .select("*")
        .eq("offer_id", sourceOfferId)
        .order("sort_order");
      if (sectionErr) throw sectionErr;

      const { data: lines, error: lineErr } = await supabase
        .from("offer_lines")
        .select("*")
        .eq("offer_id", sourceOfferId)
        .order("sort_order");
      if (lineErr) throw lineErr;

      const insertRow = buildDuplicatedOfferInsert(
        source as Offer,
        activeUser.id,
      );

      const { data: created, error: createErr } = await supabase
        .from("offers")
        .insert(insertRow)
        .select()
        .single();
      if (createErr) throw createErr;

      const sourceSections = (sections ?? []) as OfferSection[];
      const sectionIdMap = new Map<string, string>();

      if (sourceSections.length > 0) {
        const { data: newSections, error: insSecErr } = await supabase
          .from("offer_sections")
          .insert(buildDuplicatedSectionInserts(sourceSections, created.id))
          .select();
        if (insSecErr) throw insSecErr;
        const inserted = (newSections ?? []) as OfferSection[];
        sourceSections.forEach((old, i) => {
          const neu = inserted[i];
          if (neu) sectionIdMap.set(old.id, neu.id);
        });
      }

      const sourceLines = (lines ?? []) as OfferLine[];
      if (sourceLines.length > 0) {
        const { error: insLineErr } = await supabase
          .from("offer_lines")
          .insert(
            buildDuplicatedLineInserts(sourceLines, created.id, sectionIdMap),
          );
        if (insLineErr) throw insLineErr;
      }

      return created as Offer;
    },
    onSuccess: async (offer) => {
      await invalidateFor(queryClient, "offers", { offerId: offer.id });
      await invalidateFor(queryClient, "offer_lines", { offerId: offer.id });
      await invalidateFor(queryClient, "offer_sections", { offerId: offer.id });
    },
  });
}
